// The loading screen that brings every deploy to the installed app: on a cold start, and on a
// return after a while away, it looks for a new version (at most a few seconds; offline not at
// all), installs a found one and reloads once into it. Never in the middle of a study session or
// over unsaved grades: then it waits for the next safe moment. Decisions: update-policy.ts.
// At launch a version still downloading after the check's budget is waited for (START_INSTALL_MS)
// and applied at once (Axel 2026-10-21); meanwhile Peterson lines to say aloud (update-lines.tsx).
// On a cold start the same screen then shows the warm-up (shell/warmup/: the dictionary's indexes,
// the show lines, every section's code), step by step, the line filling as they finish.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useT } from '../i18n';
import { useApp } from '../state/store';
import { flushBeforeUpdate, refreshData } from '../state/actions';
import { store } from '../state/store';
import { applyUpdate, awaitInstall, checkForUpdate, readMarker, writeMarker } from './update-gate';
import { buildId } from './build-id';
import { CHECK_BUDGET_MS, START_INSTALL_MS, afterCheck, gateDecision, markerOnStart, type GateTrigger } from './update-policy';
import { onUpdateGateChange, setUpdateGateBusy, warmupView } from './startup-gates';
import { WARMUP_STEPS } from './warmup/registry';
import { startWarmup } from './warmup/start';
import type { WarmupLabel } from './warmup/step';
import './warmup/warmup.css';
import { UpdateLines } from './update-lines';

const BUILD = buildId();
const session = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

function controlled(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.clearTimeout(t);
        resolve();
      },
      { once: true },
    );
  });
}

type Phase = 'idle' | 'checking' | 'updating';

/** Look for a new version and apply it; true when the page is reloading into it. */
async function checkAndApply(trigger: GateTrigger, onUpdating: () => void): Promise<boolean> {
  try {
    const started = Date.now();
    const reg = await Promise.race([
      navigator.serviceWorker.getRegistration(),
      new Promise<undefined>((r) => window.setTimeout(() => r(undefined), CHECK_BUDGET_MS)),
    ]);
    const left = Math.max(0, CHECK_BUDGET_MS - (Date.now() - started));
    let result = await checkForUpdate(reg, left, { online: () => navigator.onLine });
    // At launch a version still downloading is waited for and applied now (Axel 2026-10-21).
    if (trigger === 'start' && result === 'timeout' && reg?.installing) {
      onUpdating();
      result = await awaitInstall(reg, START_INSTALL_MS);
    }
    if (reg && afterCheck(result, readMarker(session()), BUILD, Date.now()) === 'apply') {
      onUpdating();
      await applyUpdate(reg, {
        flush: () => flushBeforeUpdate(),
        marker: (x) => writeMarker(session(), x),
        build: BUILD,
        now: Date.now,
        controlled,
        reload: () => window.location.reload(),
      });
      return true; // the page reloads
    } else if (trigger === 'resume') {
      // no new version: the dictionary and course files may still be newer (start-up checks them itself)
      void refreshData().catch(() => undefined);
    }
  } catch {
    /* the app goes on with what it has */
  }
  return false;
}

export function UpdateGate() {
  const t = useT();
  // dev builds: ?update-gate=updating shows the updating screen for screenshots
  const [phase, setPhase] = useState<Phase>(() =>
    import.meta.env.DEV && typeof location !== 'undefined' && new URLSearchParams(location.search).get('update-gate') === 'updating' ? 'updating' : 'idle',
  );
  const running = useRef(false);
  const deferred = useRef(false);
  const hiddenAt = useRef<number | null>(null);

  useEffect(() => {
    const supported = swSupported();
    if (!supported) {
      // no update check (dev, an old browser): the warm-up at once
      startWarmup();
      return;
    }

    // A marker from the reload before: the update arrived (another build), or a loop to stop.
    const m = markerOnStart(readMarker(session()), BUILD, Date.now());
    if (!m.keep) writeMarker(session(), null);

    /** true while the page is reloading into a new version */
    const run = async (trigger: GateTrigger, hiddenForMs: number): Promise<boolean> => {
      if (running.current) return false;
      const s = store.get();
      const decision = gateDecision({
        trigger,
        hiddenForMs,
        hasController: !!navigator.serviceWorker.controller,
        online: navigator.onLine,
        inSession: s.inSession,
        unsaved: s.unsaved,
        syncState: s.sync.state,
        path: window.location.pathname,
      });
      deferred.current = decision === 'defer';
      if (decision !== 'check') return false;
      running.current = true;
      // the audio download's screen (AudioGate.tsx) waits for this one
      setUpdateGateBusy(true);
      setPhase('checking');
      const reloading = await checkAndApply(trigger, () => setPhase('updating'));
      running.current = false;
      if (!reloading) {
        setPhase('idle');
        setUpdateGateBusy(false);
      }
      return reloading;
    };

    // the warm-up once the start-up check is over (a found version reloads the page instead)
    void run('start', Infinity).then((reloading) => {
      if (!reloading) startWarmup();
    });

    // A new version that finishes downloading after the start-up check gave up (a slow 4G: the
    // dictionary and course files change with every deploy) is taken into use as soon as it is
    // installed, at the next safe moment, instead of waiting for yet another start (Axel
    // 2026-10-06: "it took so many restarts").
    let reg: ServiceWorkerRegistration | undefined;
    const onFound = () => {
      const w = reg?.installing;
      if (!w) return;
      const done = () => {
        if (w.state === 'installed') void run('resume', Infinity);
      };
      w.addEventListener('statechange', done);
      done();
    };
    void navigator.serviceWorker.getRegistration().then((r) => {
      reg = r;
      if (!r) return;
      r.addEventListener('updatefound', onFound);
      if (r.installing) onFound();
      else if (r.waiting) void run('resume', Infinity);
    });

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
        return;
      }
      const away = hiddenAt.current === null ? 0 : Date.now() - hiddenAt.current;
      hiddenAt.current = null;
      void run('resume', away);
      // Back to the app after a short while (not swiped away): look for a new version quietly, no
      // loading screen. A found one downloads in the background and is taken into use at the next
      // safe moment by the updatefound listener below (Axel 2026-10-06).
      if (away >= 10_000 && navigator.onLine) void reg?.update().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisibility);
    // A deferred check runs at the next safe moment (session over, grades written, a calm screen).
    const retry = window.setInterval(() => {
      if (deferred.current && document.visibilityState === 'visible') void run('resume', Infinity);
    }, 2000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(retry);
      reg?.removeEventListener('updatefound', onFound);
    };
  }, []);

  // the warm-up's part of the screen; never over the login screen (the work goes on behind it)
  const warm = useSyncExternalStore(onUpdateGateChange, warmupView, warmupView);
  const needsLogin = useApp((s) => s.needsLogin);
  const warming = (warm.state === 'armed' || warm.state === 'running') && !needsLogin;
  if (phase === 'idle' && !warming) return null;
  // armed: the update check is about to start (none without a service worker: the first step's line)
  const checking = phase === 'checking' || (phase === 'idle' && warm.state === 'armed' && swSupported());
  const step = phase === 'idle' && !checking ? ((warm.label as WarmupLabel | null) ?? WARMUP_STEPS[0]?.label ?? null) : null;
  const pct = Math.round(Math.min(1, Math.max(0, warm.fraction)) * 100);
  return (
    <div className="update-gate" role="status" aria-live="polite">
      <div className="update-gate-inner">
        <p className="update-gate-name">Valyrio udrir</p>
        {step ? (
          <div
            className="update-gate-line warmup-line"
            role="progressbar"
            aria-label={t.warmup.progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
          >
            <span style={{ width: `${pct}%` }} />
          </div>
        ) : (
          <div className={phase === 'updating' ? 'update-gate-line is-full' : 'update-gate-line'} aria-hidden="true">
            <span />
          </div>
        )}
        <p className="update-gate-status">{phase === 'updating' ? t.update.updating : step ? t.warmup[step] : t.update.checking}</p>
        <p className="update-gate-status app-version">{__APP_VERSION__}</p>
        {phase !== 'idle' ? <UpdateLines /> : null}
      </div>
    </div>
  );
}

function swSupported(): boolean {
  return import.meta.env.PROD && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}
